// hooks/useUserPreferences.ts - Simplified
import { useState, useEffect } from 'react';

interface UserPreferences {
  countryCode: string;
  currencyCode: string;
  locale: string;
  countryName: string;
}

const useUserPreferences = (): UserPreferences | null => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);

  useEffect(() => {
    // Get initial preferences
    const getStoredPreferences = () => {
      try {
        if (typeof window === 'undefined') return null;
        
        const userChoice = localStorage.getItem('user-locale-choice');
        if (!userChoice) return null;

        const parsed = JSON.parse(userChoice);
        return {
          countryCode: parsed.countryCode || '',
          currencyCode: parsed.currency || '',
          locale: parsed.locale || '',
          countryName: parsed.country || ''
        };
      } catch (err) {
        console.error('Error parsing user preferences:', err);
        return null;
      }
    };

    // Set initial value
    setPreferences(getStoredPreferences());

    // Listen for changes from other components
    const handleLocaleChange = (event: CustomEvent) => {
      try {
        const detail = event.detail;
        const newPrefs: UserPreferences = {
          countryCode: detail.countryCode || '',
          currencyCode: detail.currency || '',
          locale: detail.locale || '',
          countryName: detail.country || ''
        };
        setPreferences(newPrefs);
      } catch (err) {
        console.error('Error processing locale change:', err);
      }
    };

    window.addEventListener('user-locale-changed', handleLocaleChange as EventListener);
    
    return () => {
      window.removeEventListener('user-locale-changed', handleLocaleChange as EventListener);
    };
  }, []);

  return preferences;
};

export default useUserPreferences;